import React, { useState, useMemo } from 'react';
import { Calendar, Clock, Users, Award, MapPin, ChevronDown, ChevronUp, Info, Download, CalendarPlus } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const ClassScheduleView = ({ schedule }) => {
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  // Agrupar sessões por módulo (cada 2 dias = 1 módulo)
  const groupedSchedule = useMemo(() => {
    const groups = [];
    const sessions = schedule?.schedule || [];
    for (let i = 0; i < sessions.length; i += 2) {
      const day1 = sessions[i];
      const day2 = sessions[i + 1];
      const moduleNum = day1?.module_number || Math.floor(i / 2) + 1;
      groups.push({
        module: moduleNum,
        moduleName: day1?.module || day2?.module || '',
        moduleCode: day1?.module_code || '',
        moduleHours: day1?.module_hours || '',
        day1,
        day2,
        obs: day1?.obs || day2?.obs || ''
      });
    }
    return groups;
  }, [schedule]);

  if (!schedule) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const displaySchedule = showFullSchedule ? groupedSchedule : groupedSchedule.slice(0, 5);

  const formatWeekday = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { weekday: 'short' });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Matrículas Abertas', color: 'bg-green-600' },
      full: { label: 'Turma Cheia', color: 'bg-red-600' },
      in_progress: { label: 'Em Andamento', color: 'bg-blue-600' },
      completed: { label: 'Concluída', color: 'bg-gray-600' }
    };
    const config = statusConfig[status] || statusConfig.open;
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const buildCalendarEvent = (item) => {
    const pad = (n) => String(n).padStart(2, '0');
    const compact = (d, time) => {
      const dt = new Date(d);
      const [hh, mm] = (time || '08:00').split(':');
      return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(hh)}${pad(mm)}00`;
    };
    const iso = (d, time) => {
      const dt = new Date(d);
      const [hh, mm] = (time || '08:00').split(':');
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(hh)}:${pad(mm)}:00`;
    };
    const start = item.day1?.date;
    const end = item.day2?.date || item.day1?.date;
    const title = `Módulo ${item.module} - ${item.moduleName} | ${schedule.class_name || 'Formação Revit Expert'}`;
    const location = schedule.location || 'Online (Transmissão ao Vivo)';
    const details = `Formação Revit Expert${item.moduleHours ? ` • Carga: ${item.moduleHours}` : ''} • Sábado e Domingo, ${item.day1?.start_time || '08:00'} às ${item.day1?.end_time || '17:00'}.${item.obs ? ` (${item.obs})` : ''}`;
    return { start, end, title, location, details, compact, iso };
  };

  const addToGoogle = (item) => {
    const e = buildCalendarEvent(item);
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE`
      + `&text=${encodeURIComponent(e.title)}`
      + `&dates=${e.compact(e.start, item.day1?.start_time)}/${e.compact(e.end, item.day1?.end_time)}`
      + `&ctz=America/Sao_Paulo`
      + `&details=${encodeURIComponent(e.details)}`
      + `&location=${encodeURIComponent(e.location)}`;
    window.open(url, '_blank', 'noopener');
  };

  const addToOutlook = (item) => {
    const e = buildCalendarEvent(item);
    const url = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent`
      + `&subject=${encodeURIComponent(e.title)}`
      + `&startdt=${e.iso(e.start, item.day1?.start_time)}`
      + `&enddt=${e.iso(e.end, item.day1?.end_time)}`
      + `&body=${encodeURIComponent(e.details)}`
      + `&location=${encodeURIComponent(e.location)}`;
    window.open(url, '_blank', 'noopener');
  };

  const downloadICS = (item) => {
    const e = buildCalendarEvent(item);
    const stamp = e.compact(new Date().toISOString(), '00:00');
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//AJ BIM//Calendario//PT',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      `UID:modulo-${item.module}-${e.compact(e.start, item.day1?.start_time)}@bimportal.com.br`,
      `DTSTAMP:${stamp}`,
      `DTSTART;TZID=America/Sao_Paulo:${e.compact(e.start, item.day1?.start_time)}`,
      `DTEND;TZID=America/Sao_Paulo:${e.compact(e.end, item.day1?.end_time)}`,
      `SUMMARY:${e.title}`,
      `DESCRIPTION:${e.details}`,
      `LOCATION:${e.location}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `modulo-${item.module}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const red = [220, 38, 38];

    // Cabeçalho
    doc.setFillColor(24, 24, 27);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(schedule.class_name || 'Calendário da Turma', 14, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 200);
    doc.text(`Formação Revit Expert  •  ${formatDate(schedule.start_date)} até ${formatDate(schedule.end_date)}`, 14, 22);
    doc.text(`${groupedSchedule.length} módulos  •  ${schedule.location || 'Online ao Vivo'}  •  Instrutor: ${schedule.instructor || 'Alexander Justi'}`, 14, 27);

    const rows = groupedSchedule.map((item) => {
      const datas = [
        item.day1 ? `${formatShortDate(item.day1.date)} (${formatWeekday(item.day1.date)})` : '',
        item.day2 ? `${formatShortDate(item.day2.date)} (${formatWeekday(item.day2.date)})` : ''
      ].filter(Boolean).join(' e ');
      return [
        String(item.module),
        `${item.moduleName}${item.moduleHours ? `  (${item.moduleHours})` : ''}${item.obs ? `\n• ${item.obs}` : ''}`,
        datas,
        `${item.day1?.start_time || '08:00'} - ${item.day1?.end_time || '17:00'}`
      ];
    });

    autoTable(doc, {
      startY: 36,
      head: [['#', 'Módulo', 'Datas', 'Horário']],
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: red, textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8, valign: 'middle' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 95 },
        2: { cellWidth: 55 },
        3: { cellWidth: 22, halign: 'center' }
      },
      margin: { left: 14, right: 14 },
      didDrawPage: () => {
        const pageH = doc.internal.pageSize.getHeight();
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text('As datas e horários podem sofrer pequenos ajustes. Você será notificado de qualquer alteração.', 14, pageH - 8);
        doc.text('AJ | BIM  •  bimportal.com.br', 196, pageH - 8, { align: 'right' });
      }
    });

    const safeName = (schedule.class_name || 'calendario').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    doc.save(`calendario-${safeName}.pdf`);
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 mt-6" data-testid="class-schedule-view">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="text-xl text-white flex items-center space-x-2">
              <Calendar className="text-red-500" size={24} />
              <span>{schedule.class_name}</span>
            </CardTitle>
            <p className="text-sm text-gray-400 mt-2">
              {formatDate(schedule.start_date)} até {formatDate(schedule.end_date)}
            </p>
          </div>
          <div>{getStatusBadge(schedule.status)}</div>
        </div>

        {/* Informações da Turma */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-black/30 rounded-lg p-3 flex items-center space-x-3">
            <Calendar className="text-red-500" size={20} />
            <div>
              <div className="text-sm font-bold text-white">{groupedSchedule.length}</div>
              <div className="text-xs text-gray-400">Módulos</div>
            </div>
          </div>

          <div className="bg-black/30 rounded-lg p-3 flex items-center space-x-3">
            <MapPin className="text-red-500" size={20} />
            <div>
              <div className="text-sm font-bold text-white">
                {schedule.location || (schedule.modality === 'ead_live' ? 'Online ao Vivo' : 'Presencial')}
              </div>
              <div className="text-xs text-gray-400">Local</div>
            </div>
          </div>
        </div>

        {/* Botão Baixar PDF */}
        <div className="mt-4">
          <Button
            onClick={handleDownloadPDF}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            data-testid="download-schedule-pdf-btn"
          >
            <Download className="mr-2" size={18} />
            Baixar Calendário em PDF
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <h4 className="text-white font-semibold mb-4 flex items-center space-x-2">
          <Clock className="text-red-500" size={18} />
          <span>Calendário Completo - {groupedSchedule.length} Módulos</span>
        </h4>

        {/* Tabela de Calendário */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-700">
                <th className="text-left text-gray-400 text-xs font-medium py-3 px-2 w-12">#</th>
                <th className="text-left text-gray-400 text-xs font-medium py-3 px-2">Módulo</th>
                <th className="text-left text-gray-400 text-xs font-medium py-3 px-2 w-40">Datas</th>
                <th className="text-left text-gray-400 text-xs font-medium py-3 px-2 w-28">Horário</th>
              </tr>
            </thead>
            <tbody>
              {displaySchedule.map((item) => (
                <tr
                  key={`module-${item.module}`}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors"
                  data-testid={`schedule-module-row-${item.module}`}
                >
                  <td className="py-3 px-2 align-top">
                    <Badge
                      className="bg-red-600/20 text-red-400 border-red-500/30"
                      variant="outline"
                    >
                      {item.module}
                    </Badge>
                  </td>
                  <td className="py-3 px-2 align-top">
                    <div className="text-white text-sm font-medium" data-testid={`schedule-module-name-${item.module}`}>
                      {item.moduleName || item.day1?.topic?.split(' - ')[0]}
                    </div>
                    {item.moduleHours && (
                      <div className="text-xs text-red-400/80 mt-0.5">{item.moduleHours}</div>
                    )}
                    {item.obs && (
                      <div className="flex items-start gap-1.5 mt-1">
                        <Info size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-blue-300">{item.obs}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-2 align-top">
                    <div className="text-white text-sm">
                      {item.day1 && (
                        <div>{formatShortDate(item.day1.date)} <span className="text-gray-500">({formatWeekday(item.day1.date)})</span></div>
                      )}
                      {item.day2 && (
                        <div className="text-gray-400">{formatShortDate(item.day2.date)} <span className="text-gray-600">({formatWeekday(item.day2.date)})</span></div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2 align-top">
                    <div className="text-white text-sm font-mono">
                      {item.day1?.start_time} - {item.day1?.end_time}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="mt-2 inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                          data-testid={`add-to-calendar-btn-${item.module}`}
                        >
                          <CalendarPlus size={13} />
                          Agenda
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-800 border-zinc-700 text-white">
                        <DropdownMenuItem onClick={() => addToGoogle(item)} className="cursor-pointer focus:bg-zinc-700">
                          Google Agenda
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => addToOutlook(item)} className="cursor-pointer focus:bg-zinc-700">
                          Outlook
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadICS(item)} className="cursor-pointer focus:bg-zinc-700">
                          Apple / .ics
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Botão para expandir/recolher */}
        {groupedSchedule.length > 5 && (
          <div className="text-center mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFullSchedule(!showFullSchedule)}
              className="border-red-500/50 text-red-400 hover:bg-red-600/20"
              data-testid="toggle-full-schedule-btn"
            >
              {showFullSchedule ? (
                <>
                  <ChevronUp className="mr-2" size={16} />
                  Recolher Calendário
                </>
              ) : (
                <>
                  <ChevronDown className="mr-2" size={16} />
                  Ver Calendário Completo ({groupedSchedule.length - 5} módulos restantes)
                </>
              )}
            </Button>
          </div>
        )}

        <div className="mt-6 bg-red-900/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-sm text-gray-300">
            <strong className="text-red-400">Atenção:</strong> As datas e horários podem sofrer pequenos ajustes.
            Você será notificado de qualquer alteração.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClassScheduleView;
